// src/api/replayApi.ts
// Replay API — fetches historical frames for replay mode.
//
// Responsibilities:
//   - Fetch historical frames from backend (or IndexedDB cache)
//   - Provide seekToFrame() using FrameCache snapshots
//   - Playback controls (play, pause, step)
//
// Does NOT do: WS connection, state management

import type { FrameCache, Frame } from '../core/frame_cache';
import type { UIFrame } from '../core/ui_contract';
import { diffFrames } from '../core/frame_diff';

export interface ReplayRange {
  startSeq: number;
  endSeq: number;
  startTime: number;
  endTime: number;
}

export interface ReplayState {
  isLoaded: boolean;
  isPlaying: boolean;
  currentFrameId: number;
  totalFrames: number;
  range: ReplayRange | null;
  playbackSpeed: number; // 1 = real-time, 2 = 2x, 0.5 = 0.5x
}

interface ReplayApiConfig {
  frameCache: FrameCache;
  backendUrl?: string;
}

export class ReplayApi {
  private frameCache: FrameCache;
  private backendUrl: string;
  private state: ReplayState = {
    isLoaded: false,
    isPlaying: false,
    currentFrameId: 0,
    totalFrames: 0,
    range: null,
    playbackSpeed: 1,
  };
  private playbackTimer: ReturnType<typeof setTimeout> | null = null;
  private onStateChange: ((state: ReplayState) => void) | null = null;

  constructor(config: ReplayApiConfig) {
    this.frameCache = config.frameCache;
    this.backendUrl = config.backendUrl ?? '';
  }

  // ---- Public API ----

  /** Load replay data for a time range */
  async loadRange(startTime: number, endTime: number): Promise<void> {
    // Check if we have data in FrameCache already
    const cachedFrames = this.frameCache.getRange(1, this.frameCache.getStats().totalFrames);
    const inRange = cachedFrames.filter(
      f => f.generated_at_ms >= startTime && f.generated_at_ms <= endTime
    );

    if (inRange.length > 0) {
      // Use cached data
      this.state.range = {
        startSeq: inRange[0].seq,
        endSeq: inRange[inRange.length - 1].seq,
        startTime: inRange[0].generated_at_ms,
        endTime: inRange[inRange.length - 1].generated_at_ms,
      };
      this.state.totalFrames = inRange.length;
      this.state.isLoaded = true;
      this.emit();
      return;
    }

    // Fetch from backend
    if (!this.backendUrl) {
      console.warn('[ReplayApi] No backendUrl configured, cannot fetch replay data');
      return;
    }

    // TODO: Implement actual backend fetch
    // const response = await fetch(`${this.backendUrl}/replay?start=${startTime}&end=${endTime}`);
    // const frames: UIFrame[] = await response.json();
    // for (const frame of frames) {
    //   this.frameCache.push(frame.seq, frame.generated_at_ms, frame.frame_checksum, frame);
    // }
  }

  /** Seek to a specific frame by frameId */
  async seekToFrame(frameId: number): Promise<Frame | undefined> {
    if (!this.state.isLoaded) {
      console.warn('[ReplayApi] Replay not loaded');
      return undefined;
    }

    const total = this.frameCache.getStats().totalFrames;
    if (frameId < 1 || frameId > total) {
      console.warn(`[ReplayApi] frameId ${frameId} out of range [1, ${total}]`);
      return undefined;
    }

    // Use FrameCache.seekToFrameId for snapshot+delta reconstruction
    const frame = this.frameCache.seekToFrameId(frameId);
    this.state.currentFrameId = frameId;
    this.emit();
    return frame;
  }

  /** Seek to a specific timestamp */
  async seekToTime(ts: number): Promise<Frame | undefined> {
    const frame = this.frameCache.getByTime(ts);
    if (frame) {
      return this.seekToFrame(frame.frame_id);
    }
    return undefined;
  }

  /** Start playback */
  play(): void {
    if (!this.state.isLoaded) return;
    this.state.isPlaying = true;
    this.scheduleNextFrame();
    this.emit();
  }

  /** Pause playback */
  pause(): void {
    this.state.isPlaying = false;
    if (this.playbackTimer) {
      clearTimeout(this.playbackTimer);
      this.playbackTimer = null;
    }
    this.emit();
  }

  /** Step forward one frame */
  stepForward(): void {
    if (this.state.currentFrameId < this.state.totalFrames) {
      this.seekToFrame(this.state.currentFrameId + 1);
    }
  }

  /** Step backward one frame */
  stepBackward(): void {
    if (this.state.currentFrameId > 1) {
      this.seekToFrame(this.state.currentFrameId - 1);
    }
  }

  /** Set playback speed */
  setSpeed(speed: number): void {
    this.state.playbackSpeed = Math.max(0.1, Math.min(10, speed));
    this.emit();
  }

  /** Get current frame */
  getCurrentFrame(): Frame | undefined {
    if (this.state.currentFrameId === 0) return undefined;
    return this.frameCache.getById(this.state.currentFrameId);
  }

  /** Get frame at index */
  getFrame(frameId: number): Frame | undefined {
    return this.frameCache.getById(frameId);
  }

  /** Get diff between current and next frame */
  getNextDiff() {
    const current = this.getCurrentFrame();
    const next = this.frameCache.getById(this.state.currentFrameId + 1);
    if (!current || !next) return null;
    return diffFrames(current.data, next.data);
  }

  /** Subscribe to state changes */
  onUpdate(callback: (state: ReplayState) => void): () => void {
    this.onStateChange = callback;
    return () => { this.onStateChange = null; };
  }

  /** Get current state */
  getState(): ReplayState {
    return { ...this.state };
  }

  // ---- Private ----

  private scheduleNextFrame(): void {
    if (!this.state.isPlaying) return;

    const interFrameDelay = 1000 / this.state.playbackSpeed; // ms per frame at 1x

    this.playbackTimer = setTimeout(() => {
      if (this.state.currentFrameId >= this.state.totalFrames) {
        // End of replay
        this.pause();
        return;
      }
      this.seekToFrame(this.state.currentFrameId + 1);
      this.scheduleNextFrame();
    }, interFrameDelay);
  }

  private emit(): void {
    if (this.onStateChange) {
      this.onStateChange(this.getState());
    }
  }
}
